/**
 * Trybe theme.js
 * Handles: phone-model selector modal + persistence, model-based product
 * filtering (client-side, tag-driven), Theme-of-the-Month countdown,
 * and the "notify me / interest" lead capture form.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "trybe_selected_model";
  var STORAGE_TAG_KEY = "trybe_selected_model_tag";

  /* ---------------- Model selector ---------------- */

  function getSelectedModel() {
    try {
      return {
        name: window.localStorage.getItem(STORAGE_KEY) || "",
        tag: window.localStorage.getItem(STORAGE_TAG_KEY) || ""
      };
    } catch (e) {
      return { name: "", tag: "" };
    }
  }

  function setSelectedModel(name) {
    var tag = window.trybeModelToTag(name);
    try {
      window.localStorage.setItem(STORAGE_KEY, name);
      window.localStorage.setItem(STORAGE_TAG_KEY, tag);
    } catch (e) {}
    updatePillLabel();
    applyModelFilter();
    document.dispatchEvent(new CustomEvent("trybe:model-selected", { detail: { name: name, tag: tag } }));
  }

  function updatePillLabel() {
    var sel = getSelectedModel();
    document.querySelectorAll("[data-model-pill-label]").forEach(function (el) {
      el.textContent = sel.name || "Select your phone";
    });
    document.querySelectorAll("[data-model-locked-banner]").forEach(function (el) {
      el.style.display = sel.name ? "flex" : "none";
    });
    document.querySelectorAll("[data-model-locked-name]").forEach(function (el) {
      el.textContent = sel.name;
    });
  }

  function openModal() {
    var modal = document.querySelector("[data-model-modal]");
    if (!modal) return;
    modal.classList.add("is-open");
    var search = modal.querySelector("[data-model-search]");
    if (search) { search.value = ""; search.focus(); }
    renderModelList("");
  }

  function closeModal() {
    var modal = document.querySelector("[data-model-modal]");
    if (modal) modal.classList.remove("is-open");
  }

  function renderModelList(query, activeBrand) {
    var listEl = document.querySelector("[data-model-list]");
    if (!listEl || !window.TRYBE_PHONE_MODELS) return;
    query = (query || "").toLowerCase().trim();

    var brands = Object.keys(window.TRYBE_PHONE_MODELS);
    var html = "";
    var found = 0;

    brands.forEach(function (brand) {
      if (activeBrand && activeBrand !== "All" && activeBrand !== brand) return;
      var models = window.TRYBE_PHONE_MODELS[brand].filter(function (m) {
        return !query || m.toLowerCase().indexOf(query) !== -1 || brand.toLowerCase().indexOf(query) !== -1;
      });
      models.forEach(function (m) {
        found++;
        html += '<div class="model-list__item" role="option" data-model-name="' + m.replace(/"/g, "&quot;") + '">' +
          brand + " " + m + "</div>";
      });
    });

    listEl.innerHTML = found ? html : '<div class="model-list__empty">No matching phone model. Try another search.</div>';

    listEl.querySelectorAll("[data-model-name]").forEach(function (item) {
      item.addEventListener("click", function () {
        setSelectedModel(item.getAttribute("data-model-name"));
        closeModal();
      });
    });
  }

  function renderBrandTabs() {
    var tabsEl = document.querySelector("[data-model-brand-tabs]");
    if (!tabsEl || !window.TRYBE_PHONE_MODELS) return;
    var brands = ["All"].concat(Object.keys(window.TRYBE_PHONE_MODELS));
    tabsEl.innerHTML = brands.map(function (b, i) {
      return '<button type="button" class="model-brand-tab' + (i === 0 ? " is-active" : "") + '" data-brand="' + b + '">' + b + "</button>";
    }).join("");

    tabsEl.querySelectorAll("[data-brand]").forEach(function (tab) {
      tab.addEventListener("click", function () {
        tabsEl.querySelectorAll(".model-brand-tab").forEach(function (t) { t.classList.remove("is-active"); });
        tab.classList.add("is-active");
        var search = document.querySelector("[data-model-search]");
        renderModelList(search ? search.value : "", tab.getAttribute("data-brand"));
      });
    });
  }

  /* ---------------- Product-grid model filtering ---------------- */
  // Each product card renders with data-model-tags="model-iphone-15-pro model-oneplus-12" etc.
  // (set from product tags in Liquid). We hide cards that don't match the
  // selected model, and show a prompt state when no model is chosen yet.

  function applyModelFilter() {
    var sel = getSelectedModel();
    var grids = document.querySelectorAll("[data-model-filterable-grid]");
    grids.forEach(function (grid) {
      var cards = grid.querySelectorAll("[data-model-tags]");
      var visibleCount = 0;
      cards.forEach(function (card) {
        var tags = (card.getAttribute("data-model-tags") || "").split(" ");
        var universal = card.getAttribute("data-model-universal") === "true";
        var matches = universal || (sel.tag && tags.indexOf(sel.tag) !== -1);
        card.style.display = sel.name ? (matches ? "" : "none") : "none";
        if (matches && sel.name) visibleCount++;
      });
      var emptyState = grid.parentElement.querySelector("[data-model-empty-state]");
      var promptState = grid.parentElement.querySelector("[data-model-prompt-state]");
      if (promptState) promptState.style.display = sel.name ? "none" : "block";
      if (emptyState) emptyState.style.display = sel.name && visibleCount === 0 ? "block" : "none";
    });
  }

  /* ---------------- Countdown timer (Theme of the Month) ---------------- */

  function getCountdownTarget(mode, now) {
    if (mode === "week") {
      // Ends Sunday midnight (start of next Monday)
      var day = now.getDay(); // 0 = Sunday
      var daysUntilNextMonday = day === 0 ? 1 : 8 - day;
      var target = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilNextMonday, 0, 0, 0);
      return target;
    }
    // default: "month" — ends at the start of next month
    return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
  }

  function initCountdowns() {
    document.querySelectorAll("[data-countdown]").forEach(function (el) {
      var mode = el.getAttribute("data-countdown-mode") || "month";
      function tick() {
        var now = new Date();
        var endOfMonth = getCountdownTarget(mode, now);
        var diff = Math.max(0, endOfMonth - now);

        var days = Math.floor(diff / (1000 * 60 * 60 * 24));
        var hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        var mins = Math.floor((diff / (1000 * 60)) % 60);
        var secs = Math.floor((diff / 1000) % 60);

        var d = el.querySelector('[data-cd="d"]');
        var h = el.querySelector('[data-cd="h"]');
        var m = el.querySelector('[data-cd="m"]');
        var s = el.querySelector('[data-cd="s"]');
        if (d) d.textContent = String(days).padStart(2, "0");
        if (h) h.textContent = String(hours).padStart(2, "0");
        if (m) m.textContent = String(mins).padStart(2, "0");
        if (s) s.textContent = String(secs).padStart(2, "0");
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---------------- Interest / lead-capture form ---------------- */
  // Captures phone + email interest in the Theme of the Month before purchase.
  // Stores as a Shopify customer-facing "contact" style submission via the
  // theme's own /contact form endpoint (native Shopify, no app required),
  // tagged so it's filterable in Shopify admin under Marketing > Customers.

  function initInterestForms() {
    document.querySelectorAll("[data-interest-form]").forEach(function (form) {
      form.addEventListener("submit", function (evt) {
        var msgEl = form.querySelector("[data-interest-msg]");
        var phone = form.querySelector('[name="contact[phone]"]');
        var email = form.querySelector('[name="contact[email]"]');

        var phoneOk = phone && /^[6-9]\d{9}$/.test(phone.value.trim());
        var emailOk = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());

        if (!phoneOk || !emailOk) {
          evt.preventDefault();
          if (msgEl) {
            msgEl.textContent = !emailOk ? "Enter a valid email address." : "Enter a valid 10-digit Indian mobile number.";
            msgEl.className = "interest-form__msg is-error";
          }
          return;
        }
        // Let the native Shopify /contact form submit (progressive enhancement).
        // Optimistically bump the local "interested" counter for immediate feedback.
        try {
          var key = "trybe_interest_count";
          var count = parseInt(window.localStorage.getItem(key) || "0", 10) + 1;
          window.localStorage.setItem(key, String(count));
        } catch (e) {}
        if (msgEl) {
          msgEl.textContent = "You're on the list — we'll text and email you when it drops.";
          msgEl.className = "interest-form__msg is-success";
        }
      });
    });
  }

  /* ---------------- Init ---------------- */

  document.addEventListener("DOMContentLoaded", function () {
    updatePillLabel();
    applyModelFilter();
    initCountdowns();
    initInterestForms();
    renderBrandTabs();

    document.querySelectorAll("[data-model-pill]").forEach(function (btn) {
      btn.addEventListener("click", openModal);
    });
    document.querySelectorAll("[data-model-modal-close]").forEach(function (btn) {
      btn.addEventListener("click", closeModal);
    });
    document.querySelectorAll("[data-model-modal]").forEach(function (modal) {
      modal.addEventListener("click", function (e) {
        if (e.target === modal) closeModal();
      });
    });
    var search = document.querySelector("[data-model-search]");
    if (search) {
      search.addEventListener("input", function () {
        var activeTab = document.querySelector(".model-brand-tab.is-active");
        renderModelList(search.value, activeTab ? activeTab.getAttribute("data-brand") : "All");
      });
    }
    document.querySelectorAll("[data-model-change-btn]").forEach(function (btn) {
      btn.addEventListener("click", openModal);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeModal();
    });
  });
})();
